/**
 * gammaUiState.ts — MER Algo Footprints v7.1B — Phase 6.2 (micro-polish)
 *
 * View-model helper: translates a raw LiveGammaSnapshot (or absence of one)
 * into structured UI display state.
 *
 * Centralizes all status-message and visual-intent logic that was previously
 * scattered across LiveGammaCard's render branches.
 *
 * Pure function — no side effects, no React, no fetching.
 */

import { LiveGammaSnapshot } from './useGammaSnapshot';

// ─── Display state shape ──────────────────────────────────────────────────────

export type GammaDisplayStatus =
  | 'loading'       // fetch in progress, no prior data
  | 'unavailable'   // no data, reason known or unknown
  | 'stale'         // data present but age exceeds threshold
  | 'ready';        // data present and fresh

export type GammaDisplaySeverity = 'info' | 'warn' | 'muted';

export interface GammaUiState {
  status:      GammaDisplayStatus;
  severity:    GammaDisplaySeverity;
  statusLabel: string;              // human-readable single-line summary
  borderColor: string;              // CSS variable for card border
  showContent: boolean;             // whether the full metric grid should render
  snapshot:    LiveGammaSnapshot | null;
}

// ─── Reason → human text map ──────────────────────────────────────────────────

const REASON_LABELS: Record<string, string> = {
  NOT_WIRED:               '🔌 Connect TradeStation to enable live gamma analysis',
  CACHE_MISS:              'Warming up — no snapshot cached yet',
  PROVIDER_UNAVAILABLE:    'Provider temporarily unavailable',
  DATA_INVALID:            'Provider returned invalid data',
  INSUFFICIENT_CONTRACTS:  'Too few contracts to compute regime',
  INSUFFICIENT_NEAR_MONEY: 'Insufficient near-money option density',
  STALE:                   'Serving stale data — recompute in progress',
  DISABLED:                'Gamma subsystem is disabled',
};

function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Live gamma unavailable';
  return REASON_LABELS[reason] ?? `Unavailable (${reason.toLowerCase().replace(/_/g, ' ')})`;
}

// ─── Main mapping function ────────────────────────────────────────────────────

/**
 * Derives a GammaUiState from current hook outputs.
 *
 * @param loading  - hook loading flag
 * @param snapshot - latest snapshot (may be null)
 * @param error    - hook error string (may be null)
 */
export function resolveGammaUiState(
  loading:  boolean,
  snapshot: LiveGammaSnapshot | null,
  error:    string | null,
): GammaUiState {
  // Initial loading — no prior data to show
  if (loading && !snapshot) {
    return {
      status:      'loading',
      severity:    'muted',
      statusLabel: 'Fetching live gamma…',
      borderColor: 'var(--bd)',
      showContent: false,
      snapshot:    null,
    };
  }

  // Unavailable — server returned available:false, or network error, or no snapshot
  if (!snapshot || snapshot.available === false) {
    const reason = snapshot?.reason ?? null;
    return {
      status:      'unavailable',
      severity:    'muted',
      statusLabel: reasonLabel(reason ?? error),
      borderColor: 'var(--bd)',
      showContent: false,
      snapshot:    snapshot ?? null,
    };
  }

  // Stale — data is present but aged
  if (snapshot.isStale) {
    const staleSec = snapshot.computedAt
      ? Math.round((Date.now() - snapshot.computedAt) / 1000)
      : null;
    const ageLabel = staleSec !== null ? `${staleSec}s old` : 'age unknown';
    return {
      status:      'stale',
      severity:    'warn',
      statusLabel: `⚠ Stale data — ${ageLabel}. Recomputing…`,
      borderColor: 'var(--amber-bd)',
      showContent: true,
      snapshot,
    };
  }

  // Ready — fresh data
  return {
    status:      'ready',
    severity:    'info',
    statusLabel: '',
    borderColor: 'var(--bd)',
    showContent: true,
    snapshot,
  };
}
